import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { accountApi, hasValidAccessToken, setAccessToken } from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { useAuthContext } from '@/hooks/use-auth-context'

/**
 * Wrapper que protege rutas autenticadas.
 *
 * Flow al montar:
 *   1. Si ya hay JWT válido en memoria → render children directo.
 *   2. Si NO, intenta silent refresh contra tax-account
 *      (la cookie httpOnly viaja sola con withCredentials).
 *   3. Si refresh OK → render children.
 *   4. Si refresh falla → redirect a /login (preservando location).
 *
 * Si el person no tiene ninguna empresa asociada (memberships_count === 0),
 * redirige a /registrar-empresa antes de renderizar cualquier ruta
 * protegida. Esto evita que el dashboard u otras páginas exploten por
 * activeOrgId null.
 *
 * Esto cubre el caso F5 / re-abrir pestaña: la cookie httpOnly
 * sobrevive, así que el usuario no se desloguea.
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [state, setState] = useState<'checking' | 'authed' | 'guest'>(
    hasValidAccessToken() ? 'authed' : 'checking',
  )
  const auth = useAuthContext()

  useEffect(() => {
    if (state !== 'checking') return
    let cancelled = false
    ;(async () => {
      try {
        const r = await accountApi.post<{ access_token: string; expires_in: number }>(
          '/api/v1/auth/refresh',
          {},
        )
        if (cancelled) return
        if (r.data?.access_token) {
          setAccessToken(r.data.access_token)
          setState('authed')
        } else {
          setState('guest')
        }
      } catch {
        if (!cancelled) setState('guest')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [state])

  if (state === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-app">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    )
  }

  if (state === 'guest') {
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location }} />
  }

  // Person autenticado pero sin empresa → /registrar-empresa.
  // Excepción: si ya estamos en esa ruta, no loop (también no en /login).
  const onRegisterPage = location.pathname === ROUTES.REGISTER_COMPANY
  if (
    !onRegisterPage &&
    auth.needsCompanyRegistration &&
    !auth.isLoading
  ) {
    return <Navigate to={ROUTES.REGISTER_COMPANY} replace />
  }

  return <>{children}</>
}
